import { useState, useRef, useCallback, useEffect } from "react";
import Alert from "../../../../../components/alert";
import {
  Gamepad2,
  Calendar,
  MapPin,
  Crown,
  Trophy,
  Loader2,
  Trash2,
  MessagesSquare,
  Play,
} from "lucide-react";
import "../styles/single-elimination-bracket.css";
import CustomAlert from "../../../../../components/custom-alert";
import { useAlert } from "../../../../../hooks/useAlert";
import { Tournament } from "../../../../../common/services/tournament.types";
import { useAuth } from "../../../../../common/store/authStore";
import {
  updateSingleEliminationScore,
  progressSingleEliminationScore,
  shuffleTournamentBracket,
  deleteRound,
} from "../../../../../common/services/bracket";
import { startTournament } from "../../../../../common/services/tournament";
import { PopulatedMatch } from "../../../../../common/services/bracket.types";
import LiveChat from "./live-chat";

interface SingleEliminationBracketProps {
  tournament: Tournament;
  // onUpdate: () => void;
}

const SingleEliminationBracket = ({
  tournament,
  // onUpdate,
}: SingleEliminationBracketProps) => {
  const [updatingRound, setUpdatingRound] = useState<string | null>(null);
  const [showShuffleAlert, setShowShuffleAlert] = useState<boolean>(false);
  const [isShuffling, setIsShuffling] = useState(false);
  const [deletingRound, setDeletingRound] = useState<string | null>(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [roundToDelete, setRoundToDelete] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [localScores, setLocalScores] = useState<
    Record<string, { score1: number | null; score2: number | null }>
  >({});
  const { alert, showSuccess, showError } = useAlert();
  const [showChat, setShowChat] = useState(false);
  const { authenticatedUser } = useAuth();
  const [updatingScores, setUpdatingScores] = useState<Set<string>>(new Set());
  const debounceTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const isHost = tournament?.host._id === authenticatedUser?.id;
  const bracket = tournament?.bracket;
  const rounds = bracket
    ? Object.keys(bracket)
        .filter((key) => key.startsWith("round"))
        .sort()
    : [];
  const winner = bracket?.tournamentWinner;

  // Cleanup debounce timeouts on unmount
  useEffect(() => {
    const timeouts = debounceTimeouts.current;

    return () => {
      timeouts.forEach((timeout) => {
        clearTimeout(timeout);
      });
      timeouts.clear();
    };
  }, []);

  const handleShuffle = async () => {
    try {
      setIsShuffling(true);
      await shuffleTournamentBracket(tournament._id);
      setShowShuffleAlert(false);
      window.location.reload();
    } catch (error: any) {
      showError("Shuffle Failed", error.message);
    } finally {
      setIsShuffling(false);
    }
  };

  const handleStartTournament = async () => {
    try {
      setIsStarting(true);
      await startTournament(tournament._id);
      showSuccess("Tournament Started", "Tournament is now live!");
      window.location.reload();
    } catch (error: any) {
      showError("Start Failed", error.message);
    } finally {
      setIsStarting(false);
    }
  };

  const handleScoreChange = useCallback(
    async (
      roundName: string,
      matchId: string,
      participantId: string,
      score: string,
      isParticipant1: boolean,
    ) => {
      if (!tournament?._id) return;

      const scoreValue = parseInt(score) || 0;
      const uniqueKey = `${roundName}_${matchId}`;
      const scoreKey = `${uniqueKey}_${isParticipant1 ? "score1" : "score2"}`;

      // Clear any existing timeout for this score field
      const existingTimeout = debounceTimeouts.current.get(scoreKey);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // Update local state immediately for UI responsiveness
      setLocalScores((prev) => ({
        ...prev,
        [uniqueKey]: {
          ...prev[uniqueKey],
          [isParticipant1 ? "score1" : "score2"]: scoreValue,
        },
      }));

      // Set up debounced API call
      const timeout = setTimeout(async () => {
        // Remove from debounce map
        debounceTimeouts.current.delete(scoreKey);

        // Add to updating scores set
        setUpdatingScores((prev) => new Set(prev).add(scoreKey));

        try {
          const updatedMatch = await updateSingleEliminationScore(
            tournament._id,
            {
              roundName,
              matchId,
              participantId,
              score: scoreValue,
            },
          );

          // Update local state with server response to ensure consistency
          setLocalScores((prev) => ({
            ...prev,
            [uniqueKey]: {
              score1: updatedMatch.score1,
              score2: updatedMatch.score2,
            },
          }));
        } catch (error: any) {
          // Revert local state on error
          setLocalScores((prev) => {
            const newState = { ...prev };
            delete newState[uniqueKey];
            return newState;
          });
          showError("Score Update Failed", error.message);
        } finally {
          // Remove from updating scores set
          setUpdatingScores((prev) => {
            const newSet = new Set(prev);
            newSet.delete(scoreKey);
            return newSet;
          });
        }
      }, 3000); // 3 second debounce

      // Store the timeout
      debounceTimeouts.current.set(scoreKey, timeout);
    },
    [tournament?._id, showError],
  );

  const handleProgressRound = async (roundName: string) => {
    if (!tournament?._id) return;

    try {
      setUpdatingRound(roundName);
      await progressSingleEliminationScore(tournament._id, { roundName });
      showSuccess("Round Updated", "Round progressed successfully!");
      window.location.reload();
    } catch (error: any) {
      showError("Update Failed", error.message);
    } finally {
      setUpdatingRound(null);
    }
  };

  const handleDeleteRound = async () => {
    if (!tournament?._id || !roundToDelete) return;

    try {
      setDeletingRound(roundToDelete);
      await deleteRound(tournament._id, roundToDelete);
      setShowDeleteAlert(false);
      showSuccess("Round Deleted", "Round deleted successfully!");
      window.location.reload();
    } catch (error: any) {
      showError("Delete Failed", error.message);
    } finally {
      setDeletingRound(null);
      setRoundToDelete(null);
    }
  };

  const getMatchScore = (
    match: PopulatedMatch,
    scoreKey: "score1" | "score2",
    roundKey: string,
  ) => {
    const uniqueKey = `${roundKey}_${match.id}`;
    return localScores[uniqueKey]?.[scoreKey] ?? match[scoreKey];
  };

  const getMatchStatus = (match: PopulatedMatch, roundKey: string) => {
    const score1 = getMatchScore(match, "score1", roundKey);
    const score2 = getMatchScore(match, "score2", roundKey);
    if (score1 !== null && score2 !== null) return "completed";
    if (score1 !== null || score2 !== null) return "in-progress";
    return "pending";
  };

  const getWinner = (match: PopulatedMatch, roundKey: string) => {
    const score1 = getMatchScore(match, "score1", roundKey);
    const score2 = getMatchScore(match, "score2", roundKey);
    if (score1 === null || score2 === null) return null;
    return score1 > score2 ? match.participant1 : match.participant2;
  };

  const getAvatarUrl = (imageUrl: string | null, username: string) => {
    return imageUrl || `https://robohash.org/${username}?set=set2&size=200x200`;
  };

  const hasAnyScores = () => {
    if (!bracket) return false;
    return rounds.some((roundKey) => {
      const round = bracket[roundKey];
      const matches = Object.values(round) as PopulatedMatch[];
      return matches.some(
        (match) => match.score1 !== null || match.score2 !== null,
      );
    });
  };

  const areAllScoresComplete = (roundKey: string) => {
    if (!bracket) return false;
    const round = bracket[roundKey];
    const matches = Object.values(round) as PopulatedMatch[];
    return matches.every((match) => {
      const score1 = getMatchScore(match, "score1", roundKey);
      const score2 = getMatchScore(match, "score2", roundKey);
      return score1 !== null && score2 !== null;
    });
  };

  return (
    <div className="single-elimination-container">
      <div className="tournament-header">
        <div className="tournament-info">
          <h2 className="tournament-title">{tournament?.tournamentName}</h2>
          <div className="tournament-meta">
            <span className="meta-item">
              <Gamepad2 />
              {tournament?.selectedGame}
            </span>
            <span className="meta-item">
              <Calendar />
              {tournament?.convertedDateReadable}
            </span>
            <span className="meta-item">
              <MapPin />
              {tournament?.location}
            </span>
          </div>
        </div>
        {winner && (
          <div className="winner-banner">
            <Crown className="winner-icon" />
            <span>Winner: {winner.username}</span>
          </div>
        )}
        {!showChat && (
          <div className="live-chat-container">
            <p className="live-chat-text">Live chat</p>
            <div className="live-desc-container">
              <MessagesSquare />
              <span>
                Join the live chat to interact with the host, participants and
                others watching this live fixture.
              </span>
              <button
                className="join-chat-btn"
                onClick={() => setShowChat(true)}
              >
                Join Chat
              </button>
            </div>
          </div>
        )}
      </div>

      {showChat && (
        <LiveChat
          tournamentId={tournament._id}
          status={tournament.status}
          isHost={isHost}
          participants={tournament.participants || []}
          onClose={() => setShowChat(false)}
        />
      )}

      <div className="bracket-legend">
        <div className="legend-item">
          <div className="legend-color legend-pending"></div>
          <span>Pending</span>
        </div>
        <div className="legend-item">
          <div className="legend-color legend-in-progress"></div>
          <span>In Progress</span>
        </div>
        <div className="legend-item">
          <div className="legend-color legend-completed"></div>
          <span>Completed</span>
        </div>
        <div className="legend-item">
          <div className="legend-color legend-winner"></div>
          <span>Winner</span>
        </div>
      </div>

      <div className="bracket-rounds">
        {rounds.map((roundKey, roundIndex) => {
          const round = bracket?.[roundKey];
          if (!round) return null;

          const matches = Object.values(round) as PopulatedMatch[];
          const isFinalRound =
            matches.length === 1 &&
            matches[0].participant1 &&
            matches[0].participant2;
          const isLastRound = roundIndex === rounds.length - 1;

          return (
            <div key={roundKey} className="round-section">
              <div className="round-header">
                <h3 className="round-title">
                  {isFinalRound ? "Final" : `Round ${roundIndex + 1}`}
                </h3>
                <div className="round-stats">
                  <span>
                    {matches.length}{" "}
                    {matches.length === 1 ? "Match" : "Matches"}
                  </span>
                  {isHost &&
                    tournament.status !== "completed" &&
                    roundIndex !== 0 &&
                    isLastRound &&
                    (deletingRound === roundKey ? (
                      <Loader2
                        className="delete-round-icon spinner"
                        style={{
                          marginLeft: "10px",
                          color: "#ff4444",
                        }}
                      />
                    ) : (
                      <Trash2
                        className="delete-round-icon"
                        onClick={() => {
                          setRoundToDelete(roundKey);
                          setShowDeleteAlert(true);
                        }}
                        style={{
                          marginLeft: "10px",
                          cursor: "pointer",
                          color: "#ff4444",
                        }}
                      />
                    ))}
                </div>
              </div>

              <div className="matches-grid">
                {matches.map((match, matchIndex) => {
                  const status = getMatchStatus(match, roundKey);
                  const matchWinner = getWinner(match, roundKey);
                  const isByeRound = !match.participant2;

                  return (
                    <>
                      <div key={match.id} className={`match-card ${status}`}>
                        <div className="match-header">
                          <span className="match-number">
                            Match {matchIndex + 1}
                          </span>
                          {matchWinner && (
                            <div className="match-winner">
                              <Trophy />
                              <span>{matchWinner.username}</span>
                            </div>
                          )}
                        </div>

                        <div className="players-container">
                          {isByeRound ? (
                            <div className="bye-card">
                              <span>Bye Round</span>
                              <span className="bye-player">
                                {match.participant1?.username} advances
                              </span>
                            </div>
                          ) : (
                            <>
                              <div
                                className={`player-card ${matchWinner?.id === match.participant1?.id ? "winner" : ""}`}
                              >
                                <div className="player-avatar">
                                  <img
                                    src={getAvatarUrl(
                                      match.participant1?.imageUrl,
                                      match.participant1?.username,
                                    )}
                                    alt={match.participant1?.username}
                                  />
                                </div>
                                <div className="player-info">
                                  <span className="player-name">
                                    {match.participant1?.username}
                                  </span>
                                  {matchWinner?.id ===
                                    match.participant1?.id && (
                                    <span className="player-status">
                                      Winner
                                    </span>
                                  )}
                                </div>
                                <div className="player-score">
                                  {isHost && isLastRound ? (
                                    <div className="score-input-container">
                                      <input
                                        type="number"
                                        value={
                                          getMatchScore(
                                            match,
                                            "score1",
                                            roundKey,
                                          ) ?? ""
                                        }
                                        onChange={(e) =>
                                          handleScoreChange(
                                            roundKey,
                                            match.id,
                                            match.participant1.id,
                                            e.target.value,
                                            true,
                                          )
                                        }
                                        className={`score-input ${
                                          debounceTimeouts.current.has(
                                            `${roundKey}_${match.id}_score1`,
                                          )
                                            ? "score-input-pending"
                                            : ""
                                        }`}
                                        placeholder="0"
                                        min="0"
                                        disabled={updatingScores.size > 0}
                                      />
                                      {updatingScores.has(
                                        `${roundKey}_${match.id}_score1`,
                                      ) && (
                                        <Loader2 className="score-loading-spinner" />
                                      )}
                                      {debounceTimeouts.current.has(
                                        `${roundKey}_${match.id}_score1`,
                                      ) &&
                                        !updatingScores.has(
                                          `${roundKey}_${match.id}_score1`,
                                        ) && (
                                          <div className="score-pending-indicator">
                                            <div className="pending-dot"></div>
                                          </div>
                                        )}
                                    </div>
                                  ) : (
                                    <span className="score-display">
                                      {getMatchScore(
                                        match,
                                        "score1",
                                        roundKey,
                                      ) ?? "-"}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="vs-divider">
                                <span>VS</span>
                              </div>

                              <div
                                className={`player-card ${matchWinner?.id === match.participant2?.id ? "winner" : ""}`}
                              >
                                <div className="player-avatar">
                                  <img
                                    src={getAvatarUrl(
                                      match.participant2?.imageUrl,
                                      match.participant2?.username,
                                    )}
                                    alt={match.participant2?.username}
                                  />
                                </div>
                                <div className="player-info">
                                  <span className="player-name">
                                    {match.participant2?.username}
                                  </span>
                                  {matchWinner?.id ===
                                    match.participant2?.id && (
                                    <span className="player-status">
                                      Winner
                                    </span>
                                  )}
                                </div>
                                <div className="player-score">
                                  {isHost && isLastRound ? (
                                    <div className="score-input-container">
                                      <input
                                        type="number"
                                        value={
                                          getMatchScore(
                                            match,
                                            "score2",
                                            roundKey,
                                          ) ?? ""
                                        }
                                        onChange={(e) =>
                                          handleScoreChange(
                                            roundKey,
                                            match.id,
                                            match.participant2.id,
                                            e.target.value,
                                            false,
                                          )
                                        }
                                        className={`score-input ${
                                          debounceTimeouts.current.has(
                                            `${roundKey}_${match.id}_score2`,
                                          )
                                            ? "score-input-pending"
                                            : ""
                                        }`}
                                        placeholder="0"
                                        min="0"
                                        disabled={updatingScores.size > 0}
                                      />
                                      {updatingScores.has(
                                        `${roundKey}_${match.id}_score2`,
                                      ) && (
                                        <Loader2 className="score-loading-spinner" />
                                      )}
                                      {debounceTimeouts.current.has(
                                        `${roundKey}_${match.id}_score2`,
                                      ) &&
                                        !updatingScores.has(
                                          `${roundKey}_${match.id}_score2`,
                                        ) && (
                                          <div className="score-pending-indicator">
                                            <div className="pending-dot"></div>
                                          </div>
                                        )}
                                    </div>
                                  ) : (
                                    <span className="score-display">
                                      {getMatchScore(
                                        match,
                                        "score2",
                                        roundKey,
                                      ) ?? "-"}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        <div className="match-status">
                          <span className={`status-badge ${status}`}>
                            {status === "pending" && "Pending"}
                            {status === "in-progress" && "In Progress"}
                            {status === "completed" && "Completed"}
                          </span>
                        </div>
                      </div>
                    </>
                  );
                })}
              </div>

              {isFinalRound && winner && (
                <div
                  className="match-card winner-final"
                  style={{ marginTop: 30 }}
                >
                  <div className="winner-final-display">
                    <div className="winner-crown-large">
                      <Crown />
                    </div>
                    <div className="winner-avatar-large">
                      <img
                        src={getAvatarUrl(
                          tournament.bracket?.tournamentWinner?.imageUrl,
                          tournament.bracket?.tournamentWinner?.username,
                        )}
                        alt={tournament.bracket?.tournamentWinner?.username}
                      />
                    </div>
                    <div className="winner-info-large">
                      <h2 className="champion-title">Winner</h2>
                      <h3 className="champion-name">
                        {tournament.bracket?.tournamentWinner?.username}
                      </h3>
                    </div>
                    <div className="winner-stats">
                      <div className="stat-item">
                        <span className="stat-label">Tournament</span>
                        <span className="stat-value">
                          {tournament?.tournamentName}
                        </span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Game</span>
                        <span className="stat-value">
                          {tournament?.selectedGame}
                        </span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Date</span>
                        <span className="stat-value">
                          {tournament?.convertedDateReadable}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {isHost &&
                roundIndex === rounds.length - 1 &&
                !winner &&
                areAllScoresComplete(roundKey) && (
                  <div className="round-actions">
                    <button
                      className="update-round-btn"
                      onClick={() => handleProgressRound(roundKey)}
                      disabled={updatingRound === roundKey}
                    >
                      {updatingRound === roundKey
                        ? "Updating..."
                        : "Progress Round"}
                    </button>
                  </div>
                )}

              {isHost && roundIndex === 0 && !hasAnyScores() && (
                <div className="shuffle-section">
                  {tournament.status === "upcoming" && (
                    <button
                      className="shuffle-btn"
                      onClick={handleStartTournament}
                      disabled={isStarting}
                      style={{ marginRight: "10px" }}
                    >
                      {isStarting ? (
                        <>
                          <Loader2 className="spinner" />
                          Starting...
                        </>
                      ) : (
                        <>
                          <Play />
                          Start Tournament
                        </>
                      )}
                    </button>
                  )}
                  <button
                    className="shuffle-btn"
                    onClick={() => setShowShuffleAlert(true)}
                    disabled={isShuffling}
                  >
                    {isShuffling ? (
                      <>
                        <Loader2 className="spinner" />
                        Shuffling...
                      </>
                    ) : (
                      <>
                        <Gamepad2 />
                        Shuffle Participants
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <CustomAlert
        isOpen={showShuffleAlert}
        onClose={() => setShowShuffleAlert(false)}
        onConfirm={handleShuffle}
        title="Are you sure?"
        message="Shuffling will reset the current tournament brackets."
        confirmText="Yes, shuffle!"
        cancelText="Cancel"
        type="warning"
      />

      <CustomAlert
        isOpen={showDeleteAlert}
        onClose={() => {
          setShowDeleteAlert(false);
          setRoundToDelete(null);
        }}
        onConfirm={handleDeleteRound}
        title="Delete Round?"
        message="Are you sure you want to delete this round? This action cannot be undone."
        confirmText="Yes, delete!"
        cancelText="Cancel"
        type="warning"
      />

      <Alert
        show={alert.show}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        duration={4000}
        position="top-right"
      />
    </div>
  );
};

export default SingleEliminationBracket;
